import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Save,
  Shield,
  Key,
  Trash2,
  AlertTriangle,
  UserCog,
  Briefcase,
  Loader2
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Role } from '@/types';
import { getEmployeeProfile, saveEmployeeProfile } from '@/lib/employeeStorage';

export default function EmployeeEditAccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, allUsers, updateUserRole, deleteUser } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role>('analyst');
  const [selectedAnalystType, setSelectedAnalystType] = useState<'business' | 'problem_bank'>('business');
  const [profileData, setProfileData] = useState<any>(null);
  
  const employee = allUsers.find(u => u.id === id);
  
  // Load employee profile data asynchronously
  useEffect(() => {
    const loadProfile = async () => {
      if (employee) {
        setSelectedRole(employee.role);
        setLoadingProfile(true);
        try {
          const profile = await getEmployeeProfile(employee.email, employee.role);
          setProfileData(profile);
          if (profile.analyst_type) {
            setSelectedAnalystType(profile.analyst_type);
          }
        } catch (error) {
          console.error('Error loading profile:', error);
        } finally {
          setLoadingProfile(false);
        }
      }
    };
    
    loadProfile();
  }, [employee]);

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/admin/users')}>
          Back to Directory
        </Button>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check if this is the admin account (protected)
  const isAdminAccount = employee.email === 'admin@vettandventure.com';
  const isCurrentUser = currentUser?.id === employee.id;

  const handleRoleChange = async () => {
    if (selectedRole === employee.role) {
      toast({
        title: "No changes",
        description: "Role is already set to this value.",
      });
      return;
    }

    setLoading(true);
    
    const result = await updateUserRole(
      employee.id, 
      selectedRole, 
      currentUser?.id || '', 
      currentUser?.role || 'senior_analyst'
    );

    setLoading(false);
    
    if (result) {
      toast({
        title: "Error",
        description: result,
        variant: "destructive"
      });
      setSelectedRole(employee.role);
    } else {
      toast({
        title: "Role updated",
        description: `${employee.name}'s role has been changed to ${selectedRole}.`,
      });
    }
  };

  const handleAnalystTypeChange = async () => {
    if (!employee || employee.role !== 'analyst') return;
    
    setLoading(true);
    
    // Update analyst type in profile
    const profile = profileData || await getEmployeeProfile(employee.email, employee.role);
    await saveEmployeeProfile(employee.email, {
      ...profile,
      analyst_type: selectedAnalystType
    });

    setLoading(false);
    toast({
      title: "Analyst type updated",
      description: `${employee.name} is now a ${selectedAnalystType === 'business' ? 'Business Analyst' : 'Problem Bank Analyst'}.`,
    });
  };

  const handleResetPassword = () => {
    if (!newPassword || !confirmPassword) {
      toast({
        title: "Error",
        description: "Please fill in both password fields.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        title: "Error",
        description: "Password must be at least 6 characters.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords do not match.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      setLoading(false);
      setShowResetDialog(false);
      setNewPassword('');
      setConfirmPassword('');
      
      toast({
        title: "Password reset",
        description: `Password for ${employee.name} has been reset.`,
      });
    }, 1000);
  };

  const handleDeleteUser = () => {
    setLoading(true);
    
    const result = deleteUser(employee.id, currentUser?.id || '');

    setTimeout(() => {
      setLoading(false);
      setShowDeleteDialog(false);
      
      if (result) {
        toast({
          title: "Error",
          description: result,
          variant: "destructive"
        });
      } else {
        toast({
          title: "User deleted",
          description: `${employee.name} has been removed from the system.`,
        });
        navigate('/admin/users');
      }
    }, 500);
  };

  const getRoleBadgeColor = (role: string) => {
    switch(role) {
      case 'senior_analyst':
        return 'bg-purple-100 text-purple-800';
      case 'analyst':
        return 'bg-blue-100 text-blue-800';
      case 'founder':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAnalystTypeLabel = (type: 'business' | 'problem_bank' | null) => {
    if (type === 'business') return 'Business Analyst';
    if (type === 'problem_bank') return 'Problem Bank Analyst';
    return 'Not specified';
  };

  const getAnalystTypeBadgeColor = (type: 'business' | 'problem_bank' | null) => {
    if (type === 'business') return 'bg-emerald-100 text-emerald-800';
    if (type === 'problem_bank') return 'bg-amber-100 text-amber-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(`/employee/${id}`)} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Profile
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Access Management: {employee.name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-4 bg-muted/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-1">Current Role</p>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={getRoleBadgeColor(employee.role)}>
                {employee.role === 'senior_analyst' ? 'Senior Analyst' : 
                 employee.role === 'analyst' ? 'Analyst' : 
                 employee.role === 'founder' ? 'Founder' : employee.role}
              </Badge>
              {employee.role === 'analyst' && profileData?.analyst_type && (
                <Badge className={getAnalystTypeBadgeColor(profileData.analyst_type)}>
                  {getAnalystTypeLabel(profileData.analyst_type)}
                </Badge>
              )}
              <span className="text-sm text-muted-foreground ml-2">
                {employee.email}
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Change User Role
            </h3>
            
            {isAdminAccount ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  The system administrator account cannot be modified.
                </p>
              </div>
            ) : isCurrentUser ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  You cannot change your own role. Ask another administrator to do this.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>New Role</Label>
                    <Select 
                      value={selectedRole} 
                      onValueChange={(value: Role) => setSelectedRole(value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select new role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="founder">Founder</SelectItem>
                        <SelectItem value="analyst">Analyst</SelectItem>
                        <SelectItem value="senior_analyst">Senior Analyst</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      onClick={handleRoleChange} 
                      disabled={loading || selectedRole === employee.role}
                      className="w-full"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Update Role
                    </Button>
                  </div>
                </div>

                {selectedRole !== employee.role && (
                  <p className="text-sm text-amber-600">
                    Warning: Changing role will affect what this user can access.
                  </p>
                )}
              </div>
            )}
          </div>

          {employee.role === 'analyst' && !isAdminAccount && !isCurrentUser && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold flex items-center gap-2 mb-4">
                <Briefcase className="h-5 w-5" />
                Analyst Type
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Analyst Type</Label>
                  <Select 
                    value={selectedAnalystType} 
                    onValueChange={(value: 'business' | 'problem_bank') => setSelectedAnalystType(value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select analyst type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="business">Business Analyst</SelectItem>
                      <SelectItem value="problem_bank">Problem Bank Analyst</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Business Analyst: Handles pitches and squad assignments.<br />
                    Problem Bank Analyst: Reviews submissions and manages problem bank.
                  </p>
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={handleAnalystTypeChange} 
                    disabled={loading || selectedAnalystType === profileData?.analyst_type}
                    className="w-full"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Update Analyst Type
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="border-t pt-6" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Key className="h-5 w-5" />
              Password Management
            </h3>
            
            {isAdminAccount ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  The system administrator password cannot be reset through the UI.
                </p>
              </div>
            ) : (
              <Button 
                variant="outline" 
                onClick={() => setShowResetDialog(true)}
                className="w-full md:w-auto"
              >
                <Key className="h-4 w-4 mr-2" />
                Reset Password
              </Button>
            )}
          </div>

          <div className="border-t pt-6" />

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Danger Zone
            </h3>
            
            {isAdminAccount ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  The system administrator account cannot be deleted.
                </p>
              </div>
            ) : isCurrentUser ? (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  You cannot delete your own account.
                </p>
              </div>
            ) : (
              <Button 
                variant="destructive" 
                onClick={() => setShowDeleteDialog(true)}
                className="w-full md:w-auto"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete User Account
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password for {employee.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Enter a new password for this user. They will need to use this password to log in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground">
                Must be at least 6 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleResetPassword}>
              Reset Password
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {employee.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user
              account and remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}